import { Check } from "@phosphor-icons/react";
import clsx from "clsx";
import { Link, type LinkProps } from "react-router-dom";

import { SubscriptionPlanIcon } from "features/subscriptions/components";
import { PRICING_PLANS } from "features/subscriptions/constants";

import RelativeLink from "common/components/RelativeLink";
import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import {
  DialogClose,
  DialogDescription,
  DialogTitle,
} from "common/components/ui/Dialog";
import { DrawerClose } from "common/components/ui/Drawer";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useWorkspaces from "common/datahooks/useWorkspaces";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type { SubscriptionType } from "common/types";

import type { JSX, ReactNode } from "react";

const commonAlertClassName = "w-80 [&_div_p]:mt-0 [&_div]:p-2";

const SUCCESS_CONTENT: Record<
  SubscriptionType,
  {
    bgImage: string;
    headingPrefix: string;
    description: ReactNode;
  }
> = {
  rental: {
    bgImage: "bg-[url(/images/rental-success-bg.webp)]",
    headingPrefix: "Thank you for purchasing",
    description: (
      <>
        We will send you a customization form via email so you can tell us
        exactly{" "}
        <span className="text-sm-medium text-purple-600 bg-purple-50 px-1 rounded-sm">
          how to prepare your Avatar accounts.
        </span>{" "}
        If you have any questions about the process, reach out to us anytime.
      </>
    ),
  },
  user: {
    bgImage: "bg-[url(/images/outreach-success-bg.webp)]",
    headingPrefix: "Welcome to the",
    description: "Grow your LinkedIn network with fully automated campaigns",
  },
  agency: {
    bgImage: "bg-[url(/images/agency-success-bg.webp)]",
    headingPrefix: "Welcome to the",
    description:
      "You can now easily manage your accounts across multiple workspaces. Next up, you should:",
  },
};

interface SuccessChecklistProps {
  subscriptionType: SubscriptionType;
}

function SuccessChecklist({ subscriptionType }: SuccessChecklistProps) {
  let successChecklist = [
    "Review your current Outreach subscription (to avoid any double billing)",
    "Assign your Agency seats to any workspace you own",
    "That’s it - Enjoy the Agency Plan!",
  ];

  if (subscriptionType === "user") {
    successChecklist = [
      "Seamlessly connect accounts",
      "Launch inbound & outbound campaigns",
      "Unified inbox access & 3rd party integrations",
    ];
  }

  return (
    <div className="flex flex-col gap-y-2">
      {successChecklist.map((text) => (
        <div className="flex gap-x-1.5 items-start" key={text}>
          <Check className="text-green-600 mt-0.5" />
          <p className="text-xs-medium">{text}</p>
        </div>
      ))}
    </div>
  );
}

interface SuccessLargeProps {
  subscriptionType: SubscriptionType;
}

export default function SuccessLarge({ subscriptionType }: SuccessLargeProps) {
  const isDesktop = useTwBreakpoint("lg");
  const { subscription_status } = useSelectedWorkspaceContext();
  const { workspaces } = useWorkspaces();

  const firstOutreachWorkspace = workspaces.find(
    (workspace) =>
      workspace.subscription_status !== "cancelled" &&
      workspace.subscription_status !== "non_renewing",
  );

  let rightSideComponent: JSX.Element;
  let button1Data: {
    text: string;
    linkProps: LinkProps;
    isRelativeToWorkspace?: boolean;
  };

  let button2Data: {
    text: string;
    linkProps: LinkProps;
    isRelativeToWorkspace?: boolean;
  };

  if (subscriptionType === "rental") {
    const helpDeskButtonData = {
      text: "Read more about Avatars",
      linkProps: {
        to: "https://help.aimfox.com/en/articles/13618360-what-avatar-profile-elements-can-be-customized-or-not-customized-in-aimfox",
        target: "_blank",
      },
    };

    if (subscription_status === "in_trial") {
      rightSideComponent = (
        <Alert variant="gradient" className={commonAlertClassName}>
          Buying Avatar seats does not cancel your Outreach free trial
        </Alert>
      );
      button1Data = {
        text: "Review Outreach free trial",
        linkProps: { to: `settings/avatar-plan` },
        isRelativeToWorkspace: true,
      };
      button2Data = helpDeskButtonData;
    } else {
      button1Data = helpDeskButtonData;
    }
  } else if (subscriptionType === "user") {
    button1Data = {
      text: "Assign Outreach seats",
      linkProps: { to: "accounts" }, // redirect to accounts page
      isRelativeToWorkspace: true,
    };
  } else if (subscriptionType === "agency") {
    button1Data = {
      text: "Assign agency seats",
      linkProps: { to: `settings/agency` },
      isRelativeToWorkspace: true,
    };

    if (firstOutreachWorkspace) {
      rightSideComponent = (
        <Alert variant="gradient" className={commonAlertClassName}>
          You cannot use Agency seats on a workspace with an{" "}
          {PRICING_PLANS.user.name}. You can manage your Outreach subscription
          at any time
        </Alert>
      );

      button2Data = {
        text: "Review Outreach Plan",
        linkProps: {
          // redirect to outreach plan page
          to: `/workspaces/${firstOutreachWorkspace.id}/settings/outreach-plan`,
        },
      };
    } else {
      rightSideComponent = (
        <SuccessChecklist subscriptionType={subscriptionType} />
      );
    }
  }

  const Close = isDesktop ? DialogClose : DrawerClose;
  const { bgImage, headingPrefix, description } =
    SUCCESS_CONTENT[subscriptionType];

  return (
    <div className="flex flex-col m-1.5 gap-y-7 rounded-4xl shadow-md border border-black-200">
      <div
        className={clsx("h-69 w-full bg-cover bg-center rounded-3xl", bgImage)}
      />

      <div className="px-4 md:px-16 gap-y-6 pb-6 flex flex-col">
        <div className="flex flex-col md:flex-row gap-x-11 gap-y-4">
          <div className="flex gap-x-5">
            <SubscriptionPlanIcon size="lg" plan={subscriptionType} />

            <div className="flex flex-col gap-y-2">
              <div className="flex flex-col">
                <span className="text-sm-bold">{headingPrefix}</span>
                <DialogTitle className="mb-1 px-0 text-left text-3xl-bold">
                  {PRICING_PLANS[subscriptionType].name}!
                </DialogTitle>
              </div>

              <DialogDescription className="mt-0 px-0 text-left text-sm-medium">
                {description}
              </DialogDescription>

              {subscriptionType === "agency" && firstOutreachWorkspace && (
                <SuccessChecklist subscriptionType={subscriptionType} />
              )}
            </div>
          </div>

          <div
            className={clsx(
              "self-end",
              subscriptionType === "user" && firstOutreachWorkspace
                ? "pb-0"
                : "pb-3.5",
            )}
          >
            {subscriptionType === "user" && firstOutreachWorkspace && (
              <SuccessChecklist subscriptionType={subscriptionType} />
            )}

            {rightSideComponent}
          </div>
        </div>

        <div className="flex flex-col gap-y-2 items-center">
          {button1Data && (
            <Close asChild>
              <Button asChild>
                {button1Data.isRelativeToWorkspace ? (
                  <RelativeLink {...button1Data.linkProps}>
                    {button1Data.text}
                  </RelativeLink>
                ) : (
                  <Link {...button1Data.linkProps}>{button1Data.text}</Link>
                )}
              </Button>
            </Close>
          )}

          {button2Data && (
            <Button variant="link-black" asChild>
              {button2Data.isRelativeToWorkspace ? (
                <RelativeLink {...button2Data.linkProps}>
                  {button2Data.text}
                </RelativeLink>
              ) : (
                <Link {...button2Data.linkProps}>{button2Data.text}</Link>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
